/**
 * Overprøving — søkeren som mener hun oppfyller kravene selv om systemet er
 * uenig, og ber om at en saksbehandler ser på søknaden likevel.
 *
 * Mekanismen er den gamle kvalifikasjonssidens, uendret: avkryssingen lagrer en
 * kommentar på søknaden gjennom `PUT v1/overrule/attach`
 * (`overruleApi.v1OverruleAttachCommandPut`), som setter `userComment`. Feltet
 * er flagget saksbehandleren ser — en søknad med `userComment` går til manuell
 * vurdering i stedet for å stoppe på «ikke kvalifisert».
 *
 * Denne modulen holder bare den ene regelen som må leses ut av kravtreet:
 * alderskravet.
 */

import { normalizeStatus } from './labels';
import { baseRequirementType } from './requirementTypes';
import { RequirementResult } from './types';

/** Kravtypen for alder, slik den står i kravdokumentet. */
export const AGE_REQUIREMENT_TYPE = 'Age';

/**
 * Er alderskravet i orden?
 *
 * Alder er det ene kravet en saksbehandler ikke kan overprøve — er søkeren for
 * ung, hjelper ingen kommentar — og den gamle siden skjulte derfor hele
 * overprøvingen når alderskravet ikke var oppfylt (`ageOk` i `Qualification`).
 * Regelen her er den samme: har treet ingen alderskrav i det hele tatt, er den
 * oppfylt; ellers holder det at ETT av dem er oppfylt, siden alderskravet kan
 * stå i flere alternative kvalifiseringsveier.
 *
 * Kravtypen slås tilbake til grunntypen først, slik at ontologiens
 * suffiksvarianter (`Age_minIncl_18`) treffer på samme måte som `Age`.
 *
 * Informative alternativer (`informative: true`, vurdert etter at motoren
 * valgte vei) hoppes over med hele undertreet: de bærer ikke kvalifiseringen,
 * og et alderskrav som feiler der skal ikke skjule overprøvingen. Tidligere var
 * de stubber uten barn, så dette gir samme svar som før.
 */
export const ageRequirementSatisfied = (root?: RequirementResult | null): boolean => {
  let found = false;
  let passed = false;

  const visit = (node?: RequirementResult | null): void => {
    if (!node || passed || node.informative === true) return;

    if (baseRequirementType(node.requirementType) === AGE_REQUIREMENT_TYPE) {
      found = true;
      if (normalizeStatus(node.status) === 'pass') {
        passed = true;
        return;
      }
    }

    (node.allOf || []).forEach(visit);
    (node.anyOf || []).forEach(visit);
    (node.oneOf || []).forEach(visit);
    visit(node.not);
  };

  visit(root);

  return !found || passed;
};
